import type { AccessChoice, AccessSessionResponse } from '@biasafe.models';
import { config } from '@/shared/config/env';

export type AccessAudience = AccessChoice['audience'];
const key = (audience: AccessAudience) => `biasafe.access.${audience}`;

export function isPortalHost(): boolean {
  try {
    const url = config.app.clientPortalUrl;
    return Boolean(url && new URL(url).origin === window.location.origin);
  } catch {
    return false;
  }
}

export function currentAudience(): AccessAudience {
  return isPortalHost() || /^\/portal(?:\/|$)/.test(window.location.pathname)
    ? 'portal'
    : 'advisor';
}

// A reference selects an access in THIS tab. The HttpOnly cookie and the
// server's membership check authenticate it; storage is never authorization.
export function readAccess(audience = currentAudience()): {
  contextId: string;
  choice: AccessChoice;
} | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key(audience)) ?? 'null');
    return value?.choice?.audience === audience &&
      typeof value.contextId === 'string'
      ? value
      : null;
  } catch {
    return null;
  }
}

export function saveAccess(
  result: AccessSessionResponse,
  audience = currentAudience(),
) {
  if (result.selectedAccess?.audience === audience && result.contextId) {
    sessionStorage.setItem(
      key(audience),
      JSON.stringify({
        contextId: result.contextId,
        choice: result.selectedAccess,
      }),
    );
  } else {
    clearAccess(audience);
  }
}

export function clearAccess(audience = currentAudience()) {
  sessionStorage.removeItem(key(audience));
}

export function accessHeaders(audience = currentAudience()) {
  const selection = readAccess(audience);
  return {
    'X-Biasafe-Audience': audience,
    ...(selection ? { 'X-Biasafe-Context': selection.contextId } : {}),
  };
}

export const accessPage = (audience = currentAudience()) =>
  audience === 'portal' ? '/portal/access' : '/access';

export function accessLink(choice: AccessChoice): string {
  const origin =
    choice.audience === 'portal'
      ? config.app.clientPortalUrl
      : config.app.advisorUrl;
  const path = choice.audience === 'portal' ? '/portal/access' : '/access';
  const url = new URL(path, origin || window.location.origin);
  // A membership hint, never a credential. The target server checks ownership.
  url.searchParams.set('access', choice.id);
  return url.toString();
}

export function invitationAccessHint(): string | undefined {
  return new URLSearchParams(window.location.search).get('access') || undefined;
}
